import { ComponentProps, FC, useCallback, useMemo } from 'react';

import classNames from 'classnames';

import { SafeMobileCheckProvider } from '@dtci/triton/hooks';

import {
  getMaxWidthBreakpointForMediaQuery,
  getResponsiveBreakpointClassName,
  NavBarContext,
} from '../NavBar';
import { TestMetadata } from '../types';

import './NavBar.scss';

export interface NavBarProps extends ComponentProps<'nav'> {
  /** Prop to control if you want navbar to be sticky to top (default true) */
  sticky?: boolean;
  /** Custom class name passed down to nav */
  className?: string;
  /** TestMetadata used for nav */
  testMetadata?: TestMetadata;
  /** Pass in your Brand, Controls, and Utility children */
  children?: JSX.Element | JSX.Element[];
  /** Indicate at which breakpoint size the nav bar responsive behavior starts */
  responsiveBreakpoint?: 'sm' | 'md' | 'lg';
  /** Indicates if a drawer should be used for mobile experience */
  useMobileDrawer?: boolean;
}

const NavBar: FC<NavBarProps> = ({
  sticky = true,
  className,
  testMetadata,
  children,
  responsiveBreakpoint = 'sm',
  useMobileDrawer = true,
  ...otherProps
}) => {
  const getBreakpointClassName = useCallback(
    (baseClassName: string, includeBaseClass = true) =>
      getResponsiveBreakpointClassName(
        baseClassName,
        responsiveBreakpoint,
        includeBaseClass,
      ),
    [responsiveBreakpoint],
  );

  const navBarContextValue = useMemo(
    () => ({
      responsiveBreakpoint,
      getBreakpointClassName,
      useMobileDrawer,
    }),
    [getBreakpointClassName, responsiveBreakpoint, useMobileDrawer],
  );

  return (
    <NavBarContext.Provider value={navBarContextValue}>
      <SafeMobileCheckProvider
        maxWidth={getMaxWidthBreakpointForMediaQuery(responsiveBreakpoint)}
      >
        <nav
          className={classNames(
            getBreakpointClassName('sb-navbar'),
            {
              [`sb-navbar__sticky`]: sticky,
            },
            className,
          )}
          {...testMetadata}
          {...otherProps}
        >
          {children}
        </nav>
      </SafeMobileCheckProvider>
    </NavBarContext.Provider>
  );
};

export default NavBar;
